/**
 * Flattens the provided (nestable) arguments, parsing strings, numbers,
 * arrays, and truthy-valued objects into an array of strings
 * Adapted from JedWatson/classnames
 * @param {...*} strings - String or strings
 * @returns {Array.<string>} - A flattened array of strings
 */
// eslint-disable-next-line complexity
const flatten = (...args) => {
  const values = [];

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];

    if (arg) {
      const argType = typeof arg;

      if (argType === 'string') {
        values.push(...arg.split(' '));
      } else if (argType === 'number') {
        values.push(arg.toString());
      } else if (Array.isArray(arg)) {
        if (arg.length) {
          const inner = flatten(...arg);

          values.push(...inner);
        }
      } else if (argType === 'object') {
        if (arg.toString === Object.prototype.toString) {
          Object.keys(arg).forEach((key) => {
            if ({}.hasOwnProperty.call(arg, key) && arg[key]) {
              values.push(key);
            }
          });
        } else {
          values.push(arg.toString());
        }
      }
    }
  }

  return values;
};

/**
 * Apply BEM modifiers to the provided classes,
 * omitting pre-modified classes and returning a
 * valid className string
 * @param {*} classes - Classes
 * @param {...*} modifiers - Modifiers
 * @returns {Array.<string>} - A string of class names
 */
export const modify = (classes, ...modifiers) => {
  const classesFlat = flatten(classes);
  const modifiersFlat = flatten(modifiers);
  const hasModifiers = modifiersFlat.length;

  const classNames = classesFlat.reduce((result, unmodifiedClass) => {
    let modifiedClass = unmodifiedClass;

    if (hasModifiers && !unmodifiedClass.match(/--/)) {
      const modified = modifiersFlat
        .map(modifier => `${unmodifiedClass}--${modifier}`)
        .join(' ');

      modifiedClass = `${unmodifiedClass} ${modified}`;
    }

    result.push(modifiedClass);
    return result;
  }, []);

  return classNames.join(' ');
};

/**
 * Generate a function with modifiers pre-defined
 * @param {...*} modifiers - Modifiers
 * @returns {Func} - A modify() wrapper with modifiers pre-defined
 */
export const styler = (...modifiers) => className => modify(className, ...modifiers);
